import { Link } from "react-router";
import classNames from "classnames";
import type { MouseEvent, ReactNode } from "react";
import { Division, Icon, Icons } from "@ir/ui";
import type {
  BreadcrumbItem,
  BreadcrumbProps,
  BreadcrumbRouteItem,
  BreadcrumbSeparatorItem,
} from "./breadcrumb.types";
import "./Breadcrumb.scss";

/** Default separator: chevron (matches previous breadcrumb design). */
export const defaultBreadcrumbSeparator = (
  <Icon
    name={Icons.ChevronRight}
    size="small"
    color="grey"
    className="breadcrumb-separator-icon"
  />
);

export function isBreadcrumbSeparator(
  item: BreadcrumbItem,
): item is BreadcrumbSeparatorItem {
  return (
    typeof item === "object" &&
    item !== null &&
    "type" in item &&
    item.type === "separator"
  );
}

/** Route crumbs only; entries with `type: "separator"` are skipped (see `BreadcrumbSeparatorItem`). */
export function flattenBreadcrumbRoutes(
  items: readonly BreadcrumbItem[],
): BreadcrumbRouteItem[] {
  const out: BreadcrumbRouteItem[] = [];
  for (const raw of items) {
    if (isBreadcrumbSeparator(raw)) continue;
    out.push(raw);
  }
  return out;
}

function defaultItemBody(
  route: BreadcrumbRouteItem,
  isLast: boolean,
  onClick: BreadcrumbRouteItem["onClick"],
): ReactNode {
  const title = route.title;
  const showLink = !isLast && (Boolean(route.path) || Boolean(route.href));

  if (showLink && route.href) {
    return (
      <a
        href={route.href}
        className={classNames("breadcrumb-link", route.className)}
        onClick={onClick as (e: MouseEvent<HTMLAnchorElement>) => void}
      >
        {title}
      </a>
    );
  }

  if (showLink && route.path) {
    return (
      <Link
        to={route.path}
        className={classNames("breadcrumb-link", route.className)}
        onClick={onClick as (e: MouseEvent<HTMLAnchorElement>) => void}
      >
        {title}
      </Link>
    );
  }

  return (
    <Division
      className={classNames(
        {
          "breadcrumb-current": isLast,
          "breadcrumb-text": !isLast,
        },
        route.className,
      )}
      aria-current={isLast ? "page" : undefined}
    >
      {title}
    </Division>
  );
}

/**
 * Breadcrumb: `items` (`title`, `path` / `href`), optional `separator`, `itemRender`, `params`.
 */
export function Breadcrumb({
  items,
  className,
  separator,
  params,
  itemRender,
}: BreadcrumbProps) {
  const resolvedSeparator =
    separator === undefined ? defaultBreadcrumbSeparator : separator;

  const routes = flattenBreadcrumbRoutes(items);

  if (routes.length === 0) {
    return null;
  }

  return (
    <nav
      aria-label="Breadcrumb"
      className={classNames("breadcrumb", className)}
    >
      <ol className="breadcrumb-list">
        {routes.map((route, index) => {
          const isLast = index === routes.length - 1;
          const paths = routes
            .slice(0, index + 1)
            .flatMap((r) => (r.path ? [r.path] : []));

          const body = itemRender
            ? itemRender(route, params, routes, paths)
            : defaultItemBody(route, isLast, route.onClick);

          const showSeparator = index > 0 && resolvedSeparator != null;

          return (
            <li key={route.key ?? `crumb-${index}`} className="breadcrumb-item">
              {showSeparator ? (
                <span className="breadcrumb-separator" aria-hidden>
                  {typeof resolvedSeparator === "string" ? (
                    <span className="breadcrumb-separator-text">
                      {resolvedSeparator}
                    </span>
                  ) : (
                    resolvedSeparator
                  )}
                </span>
              ) : null}
              {body}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
